<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
// Admin tool for the visual Inventory floor-plan view (Sales Projects >
// Inventory tab): upload one whole-floor plan image per Tower+Floor
// (Nexceo::FloorPlan), then mark where each Nexceo::UnitInventory sits on
// it by dragging a rectangle — stored as a percentage-based
// {x,y,width,height} on the unit itself (#metadata['marker']), so it stays
// correctly positioned regardless of the image's actual resolution.
// Multi-tower is already supported by the existing tower/floor columns on
// UnitInventory — no new project-level "towers" concept needed here.
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import unitInventoryAPI from 'dashboard/api/nexceo/unitInventory';
import floorPlansAPI from 'dashboard/api/nexceo/floorPlans';
import { useProjectHeader } from 'dashboard/composables/nexceo/useProjectHeader';
import ProjectWorkspaceLayout from './ProjectWorkspaceLayout.vue';
import { projectWorkspaceTabs } from './projectWorkspaceTabs';

const route = useRoute();
const { accountId } = useAccount();
const { isAdmin } = useAdmin();
const projectId = computed(() => Number(route.params.id));
const { project } = useProjectHeader(projectId);
const workspaceTabs = computed(() =>
  projectWorkspaceTabs({
    standalone: true,
    id: route.params.id,
    accountId: accountId.value,
    agentOnly: !isAdmin.value,
  })
);

const units = ref([]);
const floorPlans = ref([]);
const isLoading = ref(true);
const error = ref(null);

// Canonical status colors — must match ProjectInventory.vue's
// STATUS_OPTIONS exactly (that admin page is the single source of truth).
const STATUS_META = {
  available: { label: 'Available', color: '#10b981' },
  hold: { label: 'On Hold', color: '#f59e0b' },
  blocked: { label: 'Blocked', color: '#f97316' },
  sold: { label: 'Sold', color: '#ef4444' },
  unavailable: { label: 'Unavailable', color: '#94a3b8' },
  landowner: { label: 'Landowner', color: '#8b5cf6' },
};
function statusColor(status) {
  return STATUS_META[status]?.color || '#64748b';
}

const towers = computed(() =>
  [...new Set(units.value.map(u => u.tower).filter(Boolean))].sort()
);
const selectedTower = ref(null);

const floorsForTower = computed(() =>
  [
    ...new Set(
      units.value
        .filter(u =>
          selectedTower.value ? u.tower === selectedTower.value : true
        )
        .map(u => u.floor)
        .filter(f => f !== null && f !== undefined)
    ),
  ].sort((a, b) => a - b)
);
const selectedFloor = ref(null);

const unitsOnFloor = computed(() =>
  units.value.filter(
    u =>
      (selectedTower.value ? u.tower === selectedTower.value : !u.tower) &&
      u.floor === selectedFloor.value
  )
);
const markedUnits = computed(() => unitsOnFloor.value.filter(u => u.marker));
const unmarkedUnits = computed(() => unitsOnFloor.value.filter(u => !u.marker));

const currentFloorPlan = computed(() =>
  floorPlans.value.find(
    fp =>
      fp.tower === (selectedTower.value || null) &&
      fp.floor === selectedFloor.value
  )
);

function selectTower(tower) {
  selectedTower.value = tower;
  selectedFloor.value = null;
}

async function loadUnits() {
  const { data } = await unitInventoryAPI.list(projectId.value);
  units.value = Array.isArray(data) ? data : [];
}

async function loadFloorPlans() {
  const { data } = await floorPlansAPI.list(projectId.value);
  floorPlans.value = Array.isArray(data) ? data : [];
}

async function load() {
  isLoading.value = true;
  error.value = null;
  try {
    await Promise.all([loadUnits(), loadFloorPlans()]);
    if (towers.value.length === 1) selectedTower.value = towers.value[0];
  } catch {
    error.value = 'Failed to load floor plans.';
  } finally {
    isLoading.value = false;
  }
}

// ── Floor plan image upload ──────────────────────────────────────────────
const floorImageInputRef = ref(null);
const isUploadingFloorImage = ref(false);
async function onFloorImageSelect(e) {
  const file = e.target.files?.[0];
  if (!file || selectedFloor.value === null) return;
  isUploadingFloorImage.value = true;
  try {
    await floorPlansAPI.upload(projectId.value, {
      tower: selectedTower.value,
      floor: selectedFloor.value,
      image: file,
    });
    await loadFloorPlans();
  } catch {
    error.value = 'Failed to upload floor plan image.';
  } finally {
    isUploadingFloorImage.value = false;
    e.target.value = '';
  }
}

// ── Marker drawing (click-drag a box on the floor plan image) ──────────
const floorImageWrapperRef = ref(null);
const drawingUnitId = ref(null);
const dragState = reactive({ active: false, startX: 0, startY: 0, x: 0, y: 0 });

function startPlacing(unitId) {
  drawingUnitId.value = drawingUnitId.value === unitId ? null : unitId;
}

function percentFromEvent(e) {
  const rect = floorImageWrapperRef.value.getBoundingClientRect();
  const x = ((e.clientX - rect.left) / rect.width) * 100;
  const y = ((e.clientY - rect.top) / rect.height) * 100;
  return {
    x: Math.min(Math.max(x, 0), 100),
    y: Math.min(Math.max(y, 0), 100),
  };
}

function onWrapperMouseDown(e) {
  if (!drawingUnitId.value) return;
  const { x, y } = percentFromEvent(e);
  dragState.active = true;
  dragState.startX = x;
  dragState.startY = y;
  dragState.x = x;
  dragState.y = y;
}

function onWrapperMouseMove(e) {
  if (!dragState.active) return;
  const { x, y } = percentFromEvent(e);
  dragState.x = x;
  dragState.y = y;
}

async function onWrapperMouseUp() {
  if (!dragState.active || !drawingUnitId.value) return;
  const rect = {
    x: Math.round(Math.min(dragState.startX, dragState.x) * 10) / 10,
    y: Math.round(Math.min(dragState.startY, dragState.y) * 10) / 10,
    width: Math.round(Math.abs(dragState.x - dragState.startX) * 10) / 10,
    height: Math.round(Math.abs(dragState.y - dragState.startY) * 10) / 10,
  };
  dragState.active = false;
  // A near-zero-size drag is almost certainly an accidental click, not an
  // intentional rectangle — leave the unit unmarked rather than saving a
  // marker no one could ever see or click on the sales-facing tab.
  if (rect.width < 1 || rect.height < 1) {
    drawingUnitId.value = null;
    return;
  }
  const unitId = drawingUnitId.value;
  drawingUnitId.value = null;
  try {
    await unitInventoryAPI.markPosition(projectId.value, unitId, rect);
    await loadUnits();
  } catch {
    error.value = 'Failed to save unit position.';
  }
}

const drawRectStyle = computed(() => {
  if (!dragState.active) return null;
  const x = Math.min(dragState.startX, dragState.x);
  const y = Math.min(dragState.startY, dragState.y);
  const width = Math.abs(dragState.x - dragState.startX);
  const height = Math.abs(dragState.y - dragState.startY);
  return {
    left: `${x}%`,
    top: `${y}%`,
    width: `${width}%`,
    height: `${height}%`,
  };
});

function markerStyle(unit) {
  const m = unit.marker;
  if (!m) return null;
  return {
    left: `${m.x}%`,
    top: `${m.y}%`,
    width: `${m.width}%`,
    height: `${m.height}%`,
    backgroundColor: `${statusColor(unit.status)}33`,
    borderColor: statusColor(unit.status),
  };
}

// ── Per-unit individual floor plan image ────────────────────────────────
const unitImageInputRefs = ref({});
function setUnitImageInputRef(unitId, el) {
  if (el) unitImageInputRefs.value[unitId] = el;
  else delete unitImageInputRefs.value[unitId];
}
async function onUnitImageSelect(unit, e) {
  const file = e.target.files?.[0];
  if (!file) return;
  try {
    await unitInventoryAPI.uploadFloorPlanImage(projectId.value, unit.id, file);
    await loadUnits();
  } catch {
    error.value = `Failed to upload floor plan for ${unit.unit_no}.`;
  } finally {
    e.target.value = '';
  }
}

onMounted(load);
</script>

<template>
  <ProjectWorkspaceLayout
    show-chrome
    :project="project"
    :tabs="workspaceTabs"
    active-tab-key="inventory"
  >
    <div
      class="flex items-center gap-3 px-4 py-3 border-b border-n-weak shrink-0 bg-n-background"
    >
      <span class="i-lucide-map size-4 text-woot-500 shrink-0" />
      <h2 class="text-sm font-semibold text-n-slate-12">Floor Plans</h2>
      <span class="text-xs text-n-slate-7">
        Upload a floor plan image per tower/floor, then drag a box over each
        unit to mark its position.
      </span>
    </div>

    <div v-if="isLoading" class="flex justify-center py-16">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7" />
    </div>

    <div
      v-else-if="error"
      class="m-4 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-xs text-red-700 dark:text-red-400"
    >
      {{ error }}
    </div>

    <div v-else class="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
      <!-- Tower / Floor pickers -->
      <div class="flex flex-col gap-2">
        <div v-if="towers.length" class="flex flex-wrap gap-2">
          <button
            v-for="tower in towers"
            :key="tower"
            type="button"
            class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
            :class="
              selectedTower === tower
                ? 'bg-woot-500 text-white'
                : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3'
            "
            @click="selectTower(tower)"
          >
            Tower {{ tower }}
          </button>
        </div>
        <div v-if="floorsForTower.length" class="flex flex-wrap gap-2">
          <button
            v-for="floor in floorsForTower"
            :key="floor"
            type="button"
            class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
            :class="
              selectedFloor === floor
                ? 'bg-woot-100 text-woot-700 dark:bg-woot-500/20 dark:text-woot-400'
                : 'bg-n-alpha-1 text-n-slate-9 hover:bg-n-alpha-2'
            "
            @click="selectedFloor = floor"
          >
            Floor {{ floor }}
          </button>
        </div>
      </div>

      <div v-if="selectedFloor === null" class="text-center py-16">
        <span
          class="i-lucide-layers size-8 text-n-slate-6 mx-auto block mb-2"
        />
        <p class="text-sm font-medium text-n-slate-9">
          Pick a tower and floor to manage its floor plan.
        </p>
      </div>

      <template v-else>
        <!-- Upload / replace floor plan image -->
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-xs font-medium text-n-slate-10 hover:bg-n-alpha-2 transition-colors disabled:opacity-50"
            :disabled="isUploadingFloorImage"
            @click="floorImageInputRef?.click()"
          >
            <span
              class="size-3.5"
              :class="
                isUploadingFloorImage
                  ? 'i-lucide-loader-2 animate-spin'
                  : 'i-lucide-upload-cloud'
              "
            />
            {{
              currentFloorPlan
                ? 'Replace Floor Plan Image'
                : 'Upload Floor Plan Image'
            }}
          </button>
          <input
            ref="floorImageInputRef"
            type="file"
            accept=".jpg,.jpeg,.png,.webp"
            class="hidden"
            @change="onFloorImageSelect"
          />
        </div>

        <!-- Floor plan image with draggable unit markers -->
        <div v-if="currentFloorPlan?.image_url" class="flex flex-col gap-2">
          <p
            v-if="drawingUnitId"
            class="text-xs text-woot-600 dark:text-woot-400"
          >
            Click and drag on the image to mark this unit's position. Click
            "Place on Floor Plan" again to cancel.
          </p>
          <div
            ref="floorImageWrapperRef"
            class="relative rounded-xl overflow-hidden border border-n-weak select-none"
            :class="drawingUnitId ? 'cursor-crosshair' : ''"
            @mousedown="onWrapperMouseDown"
            @mousemove="onWrapperMouseMove"
            @mouseup="onWrapperMouseUp"
            @mouseleave="dragState.active = false"
          >
            <img
              :src="currentFloorPlan.image_url"
              alt="Floor plan"
              class="w-full h-auto block pointer-events-none"
              draggable="false"
            />
            <div
              v-for="unit in markedUnits"
              :key="unit.id"
              class="absolute border-2 flex items-center justify-center text-[10px] font-semibold text-n-slate-12 cursor-pointer"
              :style="markerStyle(unit)"
              :title="`${unit.unit_no} — ${unit.status}`"
              @click.stop="startPlacing(unit.id)"
            >
              {{ unit.unit_no }}
            </div>
            <div
              v-if="drawRectStyle"
              class="absolute border-2 border-woot-500 bg-woot-500/20 pointer-events-none"
              :style="drawRectStyle"
            />
          </div>
        </div>
        <p v-else class="text-xs text-n-slate-7">
          Upload a floor plan image above to start marking unit positions.
        </p>

        <!-- Units on this floor -->
        <div class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            Units on Floor {{ selectedFloor }} ({{ unitsOnFloor.length }})
          </h3>

          <div v-if="unmarkedUnits.length" class="flex flex-col gap-1.5">
            <p class="text-xs text-n-slate-7">
              Not yet placed on the floor plan:
            </p>
            <div class="flex flex-wrap gap-2">
              <div
                v-for="unit in unmarkedUnits"
                :key="unit.id"
                class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-n-weak"
              >
                <span
                  class="size-2 rounded-full shrink-0"
                  :style="{ backgroundColor: statusColor(unit.status) }"
                />
                <span class="text-xs font-medium text-n-slate-12">{{
                  unit.unit_no
                }}</span>
                <button
                  type="button"
                  class="text-xs font-medium text-woot-600 dark:text-woot-400 hover:underline"
                  :disabled="!currentFloorPlan?.image_url"
                  @click="startPlacing(unit.id)"
                >
                  {{
                    drawingUnitId === unit.id ? 'Cancel' : 'Place on Floor Plan'
                  }}
                </button>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-1.5">
            <p v-if="markedUnits.length" class="text-xs text-n-slate-7">
              Individual floor plan image (unit's own interior layout, shown
              when a customer opens that unit):
            </p>
            <div
              v-for="unit in unitsOnFloor"
              :key="`img-${unit.id}`"
              class="flex items-center gap-2"
            >
              <span class="text-xs font-medium text-n-slate-12 w-16 shrink-0">{{
                unit.unit_no
              }}</span>
              <img
                v-if="unit.floor_plan_image_url"
                :src="unit.floor_plan_image_url"
                alt=""
                class="size-8 rounded object-cover border border-n-weak shrink-0"
              />
              <button
                type="button"
                class="text-xs font-medium text-woot-600 dark:text-woot-400 hover:underline"
                @click="unitImageInputRefs[unit.id]?.click()"
              >
                {{ unit.floor_plan_image_url ? 'Replace' : 'Upload' }} Unit
                Floor Plan
              </button>
              <input
                :ref="el => setUnitImageInputRef(unit.id, el)"
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                class="hidden"
                @change="onUnitImageSelect(unit, $event)"
              />
            </div>
          </div>
        </div>
      </template>
    </div>
  </ProjectWorkspaceLayout>
</template>
